/**
 * Resource Library API
 * 对接后端 /api/v1/projects/{project_id}/resource-library/*
 * 角色/场景/道具列表已合并至 project-elements API，此处为兼容层
 */

import apiClient from './client';
import { projectElementsApi } from './project-elements';
import type { ResourceSortField, ResourceSortOrder } from '@/lib/resources/resource-sort';

export type ResourceLibraryType = 'character' | 'location' | 'prop' | 'footage';

export interface ResourceLibraryQueryParams {
  onlyOwnedByCurrentEpisode?: boolean;
  onlyLinkedToCurrentEpisode?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
  includeSubprojects?: boolean;
  /** MIME 类型筛选，仅文件列表使用（兼容旧调用） */
  mimeType?: string;
  /** 媒体大类筛选：image | video | audio */
  mediaKind?: string;
  /** 多值媒体大类，逗号分隔或数组；与 mediaKind 互斥 */
  mediaKinds?: string | string[];
  /** 筛选参数 */
  gender?: string;
  race?: string;
  visualAge?: string;
  timeOfDay?: string;
  propType?: string;
  roleType?: number;
  isOffscreen?: boolean;
  sortBy?: ResourceSortField;
  sortOrder?: ResourceSortOrder;
}

/** 文件资源列表项对应 GET /projects/{parent_project_id}/footage-resources */
export interface FootageResourceListItem {
  projectResourceId: string;
  parentProjectId: string;
  fileUrl: string;
  /** 列表展示用；image=缩略图或 fileUrl，video=缩略图或 null，audio=null */
  thumbnailUrl: string | null;
  name: string;
  description?: string | null;
  mimeType?: string | null;
  mediaKind?: 'image' | 'video' | 'audio' | null;
  originalFileName?: string | null;
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
  duration?: number | null;
  frameRate?: number | null;
  format?: string | null;
  humanAssetType?: number | null;
  humanAssetStatus?: number | null;
  /** 当前身份能否改这条实拍。缺省按能改。 */
  canManageAsset?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceInTag {
  projectResourceId: string;
  projectId: string;
  name: string;
  type: string;
  description?: string | null;
  fileUrl: string;
  thumbnailUrl?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  width?: number | null;
  height?: number | null;
  usageCount?: number;
  aiJobId?: string | null;
  versionSeq?: number | null;
  editDisplayName?: string | null;
  humanAssetType?: number | null;
  humanAssetStatus?: number | null;
  createdAt: string;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
}

export interface ElementTag {
  tagId: string;
  tagKey: string;
  tagLabel: string;
  description?: string | null;
  displayOrder: number;
  resources: ResourceInTag[];
}

export interface ResourceLibraryCharacter {
  characterId: string;
  screenplayId: string;
  name: string;
  description?: string | null;
  characterImageUrl?: string | null;
  /** 默认装扮的定稿缩略图，characterImageUrl 为空时使用 */
  defaultThumbnailUrl?: string | null;
  roleType: string;
  isOffscreen?: boolean;
  lineCount?: number;
  appearanceCount?: number;
  hasTags: boolean;
  defaultTagId?: string | null;
  tags: ElementTag[];
  createdAt: string;
  updatedAt: string;
}

export interface ResourceLibraryLocation {
  locationId: string;
  screenplayId: string;
  name: string;
  location?: string | null;
  description?: string | null;
  locationImageUrl?: string | null;
  /** 默认装扮缩略图URL，locationImageUrl 为空时使用 */
  defaultThumbnailUrl?: string | null;
  hasTags: boolean;
  defaultTagId?: string | null;
  tags: ElementTag[];
  createdAt: string;
  updatedAt: string;
}

export interface ResourceLibraryProp {
  propId: string;
  screenplayId: string;
  name: string;
  description?: string | null;
  propImageUrl?: string | null;
  /** 默认装扮缩略图URL，propImageUrl 为空时使用 */
  defaultThumbnailUrl?: string | null;
  hasTags: boolean;
  defaultTagId?: string | null;
  tags: ElementTag[];
  createdAt: string;
  updatedAt: string;
}

export interface ResourceLibraryFootageResource {
  projectResourceId: string;
  projectId: string;
  name: string;
  description?: string | null;
  fileUrl: string;
  thumbnailUrl?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  width?: number | null;
  height?: number | null;
  elementTagId?: string | null;
  elementName?: string | null;
  tagLabel?: string | null;
  usageCount?: number;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

export interface PaginatedResourceLibraryResponse<TItem> {
  items: TItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TagResourcesQueryParams {
  page?: number;
  pageSize?: number;
}

function toParams(params?: ResourceLibraryQueryParams) {
  if (!params) return undefined;
  return {
    search: params.search,
    onlyOwnedByCurrentEpisode: params.onlyOwnedByCurrentEpisode,
    onlyLinkedToCurrentEpisode: params.onlyLinkedToCurrentEpisode,
    page: params.page,
    pageSize: params.pageSize,
    includeSubprojects: params.includeSubprojects,
    mimeType: params.mimeType,
    mediaKind: params.mediaKind,
    mediaKinds: params.mediaKinds,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };
}

function mapElementTagToResourceLibraryTag(
  tag: {
    tagId: string;
    tagLabel: string;
    tagImageUrl?: string | null;
    resourceUrl?: string | null;
    selectedResourceId?: string | null;
  },
  projectId: string,
  resourceType: 'character' | 'location' | 'prop' = 'character'
): ElementTag {
  const tagDisplayUrl = tag.tagImageUrl ?? tag.resourceUrl;
  const resources: ResourceInTag[] = tagDisplayUrl
    ? [
        {
          projectResourceId: tag.selectedResourceId ?? '',
          projectId,
          name: '',
          type: resourceType,
          fileUrl: tagDisplayUrl,
          thumbnailUrl: tagDisplayUrl,
          fileSize: null,
          mimeType: null,
          width: null,
          height: null,
          usageCount: 0,
          aiJobId: null,
          createdAt: new Date().toISOString(),
          metadata: {},
        },
      ]
    : [];
  return {
    tagId: tag.tagId,
    tagKey: tag.tagId,
    tagLabel: tag.tagLabel,
    description: null,
    displayOrder: 0,
    resources,
  };
}

export const resourceLibraryApi = {
  async getCharacters(
    projectId: string,
    params?: ResourceLibraryQueryParams
  ): Promise<PaginatedResourceLibraryResponse<ResourceLibraryCharacter>> {
    const res = await projectElementsApi.getCharacters(projectId, {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 100,
      search: params?.search,
      includeTags: true,
      gender: params?.gender,
      onlyOwnedByCurrentEpisode: params?.onlyOwnedByCurrentEpisode,
      onlyLinkedToCurrentEpisode: params?.onlyLinkedToCurrentEpisode,
      race: params?.race,
      visualAge: params?.visualAge,
      roleType: params?.roleType,
      isOffscreen: params?.isOffscreen,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    });
    const items: ResourceLibraryCharacter[] = res.items.map((c) => ({
      characterId: c.characterId,
      screenplayId: c.parentProjectId,
      name: c.name,
      description: c.description ?? null,
      characterImageUrl: c.coverUrl ?? null,
      defaultThumbnailUrl: c.coverUrl ?? null,
      roleType: '',
      isOffscreen: c.isOffscreen,
      lineCount: 0,
      appearanceCount: 0,
      hasTags: c.tagCount > 0,
      defaultTagId: c.defaultTagId ?? null,
      tags: (c.tags ?? []).map((t) =>
        mapElementTagToResourceLibraryTag(t, c.parentProjectId, 'character')
      ),
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
    }));
    return {
      items,
      total: res.total,
      page: res.page,
      pageSize: res.pageSize,
      totalPages: res.totalPages,
    };
  },

  async getLocations(
    projectId: string,
    params?: ResourceLibraryQueryParams
  ): Promise<PaginatedResourceLibraryResponse<ResourceLibraryLocation>> {
    const res = await projectElementsApi.getLocations(projectId, {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 100,
      search: params?.search,
      includeTags: true,
      timeOfDay: params?.timeOfDay,
      onlyOwnedByCurrentEpisode: params?.onlyOwnedByCurrentEpisode,
      onlyLinkedToCurrentEpisode: params?.onlyLinkedToCurrentEpisode,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    });
    const items: ResourceLibraryLocation[] = res.items.map((l) => ({
      locationId: l.locationId,
      screenplayId: l.parentProjectId,
      name: l.name,
      location: null,
      description: l.description ?? null,
      locationImageUrl: l.coverUrl ?? null,
      defaultThumbnailUrl: l.coverUrl ?? null,
      hasTags: l.tagCount > 0,
      defaultTagId: l.defaultTagId ?? null,
      tags: (l.tags ?? []).map((t) =>
        mapElementTagToResourceLibraryTag(t, l.parentProjectId, 'location')
      ),
      createdAt: l.createdAt,
      updatedAt: l.updatedAt,
    }));
    return {
      items,
      total: res.total,
      page: res.page,
      pageSize: res.pageSize,
      totalPages: res.totalPages,
    };
  },

  async getProps(
    projectId: string,
    params?: ResourceLibraryQueryParams
  ): Promise<PaginatedResourceLibraryResponse<ResourceLibraryProp>> {
    const res = await projectElementsApi.getProps(projectId, {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 100,
      search: params?.search,
      includeTags: true,
      propType: params?.propType,
      onlyOwnedByCurrentEpisode: params?.onlyOwnedByCurrentEpisode,
      onlyLinkedToCurrentEpisode: params?.onlyLinkedToCurrentEpisode,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    });
    const items: ResourceLibraryProp[] = res.items.map((p) => ({
      propId: p.propId,
      screenplayId: p.parentProjectId,
      name: p.name,
      description: p.description ?? null,
      propImageUrl: p.coverUrl ?? null,
      defaultThumbnailUrl: p.coverUrl ?? null,
      hasTags: p.tagCount > 0,
      defaultTagId: p.defaultTagId ?? null,
      tags: (p.tags ?? []).map((t) =>
        mapElementTagToResourceLibraryTag(t, p.parentProjectId, 'prop')
      ),
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
    }));
    return {
      items,
      total: res.total,
      page: res.page,
      pageSize: res.pageSize,
      totalPages: res.totalPages,
    };
  },

  /**
   * 获取文件资源列表
   * @param parentProjectId 父级项目 ID，调用方必须传入（资源表 project_id 即父级项目 ID）
   * @see GET /api/v1/projects/{parent_project_id}/footage-resources
   */
  async getFootageResources(
    parentProjectId: string,
    params?: ResourceLibraryQueryParams
  ): Promise<PaginatedResourceLibraryResponse<FootageResourceListItem>> {
    const nextParams = { ...params };
    if (Array.isArray(nextParams.mediaKinds)) {
      nextParams.mediaKinds = [...new Set(nextParams.mediaKinds)].sort().join(',');
    }
    return apiClient.get(`/projects/${parentProjectId}/footage-resources`, {
      params: toParams(nextParams),
    });
  },

  async getResourcesByTag(
    projectId: string,
    tagId: string,
    params?: TagResourcesQueryParams
  ): Promise<PaginatedResourceLibraryResponse<ResourceInTag>> {
    return apiClient.get(`/projects/${projectId}/resources`, {
      params: {
        elementTagId: tagId,
        page: params?.page,
        pageSize: params?.pageSize,
      },
    });
  },

  async getFilterOptions(projectId: string, elementType?: string) {
    return projectElementsApi.getFilterOptions(projectId, elementType);
  },
};
